import { useEffect, useMemo, useState } from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'
import styled from 'styled-components'

import { Button, Loader, Message } from '@/components'
import { AceEditorField } from '@/components/AceEditor/AceEditorField'
import { Flex } from '@/components/Layout'
import { SelectFormField } from '@/components/UberForm'
import { IdCodeName } from '@/endpoints/schemas'
import { useAppContext } from '@/hooks'
import { useDetailTabContext } from '@/pages/User/pages/Home/components/DetailTab'
import { useGenerateSqlPreviewUsingGetQuery } from '@/rtkApi/mddApi'
import {
	StructureDto,
	useGetNodeHistoryDetailByVersionUsingGetQuery,
} from '@/rtkApi/mddApiSliceGenerated'
import { FormValue } from '@/types'

import { formatHistoryData } from './previewHistoryUtils'
import { useHistoryPreviewVersion } from './useHistoryPreviewVersion'

/** Ace maximize toolbar height; subtract so last lines are not clipped. */
const PREVIEW_ACE_TOOLBAR_OFFSET_PX = 32

function formatPreviewQueryError(error: unknown): string | null {
	if (error == null) return null
	if (typeof error === 'object' && error !== null) {
		if ('data' in error) {
			const d = (error as { data?: unknown }).data
			if (d && typeof d === 'object' && d !== null && 'errorDescription' in d) {
				const ed = (d as { errorDescription?: unknown }).errorDescription
				if (typeof ed === 'string') return ed
			}
			if (typeof d === 'string') return d
		}
		if (
			'message' in error &&
			typeof (error as { message?: unknown }).message === 'string'
		) {
			return (error as { message: string }).message
		}
	}
	return String(error)
}

interface PreviewProps {
	node: StructureDto
	objectTypes?: IdCodeName[]
	showObjectTypes?: boolean
}
export const Preview = ({
	node,
	showObjectTypes,
	objectTypes,
}: PreviewProps) => {
	const [isRawView, setIsRawView] = useState(false)

	const [objectTypeId, setObjectTypeId] = useState<FormValue>(
		objectTypes?.[0]?.id || null,
	)
	const { t } = useAppContext()
	const { state: { envId } } = useDetailTabContext()
	const { isHistoryPreview, previewVersion } = useHistoryPreviewVersion()

	useEffect(() => {
		if (objectTypes?.length && !objectTypeId) {
			setObjectTypeId(objectTypes[0]?.id || null)
		}
	}, [objectTypes, objectTypeId])

	useEffect(() => {
		if (isHistoryPreview) {
			setIsRawView(true)
			return
		}
		setIsRawView(false)
	}, [isHistoryPreview, previewVersion])

	const {
		data: sql,
		isLoading: isSqlLoading,
		error: sqlError,
	} = useGenerateSqlPreviewUsingGetQuery(
		{
			structureId: (node.id as number) || 0,
			objectSettingsId: objectTypeId ? (objectTypeId as number) : undefined,
		},
		{
			skip: !node?.id || isHistoryPreview,
			refetchOnMountOrArgChange: true,
		},
	)

	const {
		data: historyDetail,
		isLoading: isHistoryLoading,
		error: historyError,
	} = useGetNodeHistoryDetailByVersionUsingGetQuery(
		{
			structureId: (node.id as number) || 0,
			version: previewVersion ?? 0,
			envId,
		},
		{
			skip: !node?.id || !isHistoryPreview || previewVersion == null,
			refetchOnMountOrArgChange: true,
		},
	)

	const loading = isHistoryPreview ? isHistoryLoading : isSqlLoading

	const previewError = useMemo(() => {
		const error = isHistoryPreview ? historyError : sqlError
		return formatPreviewQueryError(error)
	}, [historyError, isHistoryPreview, sqlError])

	const historyJson = useMemo(
		() => formatHistoryData(historyDetail?.data),
		[historyDetail?.data],
	)

	const handleSwitchView = () => {
		setIsRawView(!isRawView)
	}

	const isObjectTypeValid = !showObjectTypes || objectTypeId

	return loading ? (
		<Loader loaded={false} />
	) : (
		<PreviewContainer>
			<ActionBarContainer>
				<SwitchButton>
					<Button
						onClick={handleSwitchView}
						schema={!isRawView ? 'primary' : 'primaryOutlined'}
						testId="script"
						disabled={isHistoryPreview}
					>
						{t('SCRIPT')}
					</Button>
					<Button
						onClick={handleSwitchView}
						schema={isRawView ? 'primary' : 'primaryOutlined'}
						testId="raw-json"
					>
						{t('RAW_JSON')}
					</Button>
				</SwitchButton>
				{showObjectTypes && !isHistoryPreview && (
					<ObjectTypeSelect
						formless={true}
						name="objectType"
						options={objectTypes || []}
						valueKey="id"
						isNumeric
						allowEmpty={false}
						clearable={false}
						onChange={(e) => {
							setObjectTypeId(e ? e : null)
						}}
						initialValue={objectTypeId}
						labelKey="name"
					/>
				)}
			</ActionBarContainer>

			<ContentArea>
				{previewError && (
					<Message
						type="error"
						header={t('DDL_CREATION_FAILED')}
						message={previewError}
					/>
				)}
				{!isHistoryPreview && !isObjectTypeValid && (
					<Message
						type="error"
						header={t('DDL_CREATION_FAILED')}
						message={t('PREVIEW_ERROR_OBJECT_TYPE')}
					/>
				)}
				{!isHistoryPreview &&
					sql?.errors?.map((err) => (
						<Message key={err} type="error" header={t('ERROR')} message={err} />
					))}
				{(isHistoryPreview || (isObjectTypeValid && !previewError)) && (
					<FullscreenSql>
						<AutoSizer>
							{({ height, width }: { height: number; width: number }) => {
								const editorHeight = Math.max(
									0,
									height - PREVIEW_ACE_TOOLBAR_OFFSET_PX,
								)
								return (
									<div style={{ height, width }}>
										{isRawView ? (
											<AceEditorField
												name="script"
												value={
													isHistoryPreview
														? historyJson
														: JSON.stringify(sql?.templateDataMap, null, 2)
												}
												disabled
												mode="json"
												height={editorHeight}
											/>
										) : (
											<AceEditorField
												name="script"
												value={sql?.sql}
												disabled
												mode="sql"
												height={editorHeight}
											/>
										)}
									</div>
								)
							}}
						</AutoSizer>
					</FullscreenSql>
				)}
			</ContentArea>
		</PreviewContainer>
	)
}

const PreviewContainer = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	height: 100%;
	overflow: hidden;
	padding-top: 1rem;
	box-sizing: border-box;
`

const ContentArea = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	padding: 16px 32px 16px 16px;
	overflow: hidden;
	min-height: 0;
`

const FullscreenSql = styled.div`
	flex: 1;
	width: 100%;
	min-height: 0;
	overflow: hidden;
`

const ActionBarContainer = styled(Flex)`
	flex-shrink: 0;
`

const ObjectTypeSelect = styled(SelectFormField)`
	margin: 1px;
	max-width: 250px;
`

const SwitchButton = styled.div`
	flex: 0;
	border-radius: 1.25rem;
	display: flex;
	margin: 0 1.25rem;

	button {
		&:first-child {
			border-radius: 1.25rem 0 0 1.25rem;
		}
		&:last-child {
			border-radius: 0 1.25rem 1.25rem 0;
		}
	}
`
